import {
  semanticColors,
  spacing,
  typography
} from '@moderneinc/design-system-tokens';
import { Box, Stack, SvgIcon, Typography } from '@mui/material';
import { type FunctionComponent, useState } from 'react';
import { getIconAssetsByKey } from '../../../icons/icon-assets';
import { CopyPasta } from '../copy-pasta/copy-pasta.component';
import { ERROR_SURFACE_SX } from '../error-surface/error-surface.styles';
import { ExpandCollapse } from '../expand-collapse/expand-collapse.component';

type ErrorDetailsDisclosureProps = {
  errorDetails: string;
};

const PRE_MAX_HEIGHT = 400;
const { icon: ERROR_ICON, sxColor: ERROR_ICON_COLOR } =
  getIconAssetsByKey('ERROR');

export const ErrorDetailsDisclosure: FunctionComponent<
  ErrorDetailsDisclosureProps
> = ({ errorDetails }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Stack
      sx={{
        alignItems: 'center',
        gap: `${spacing.spacing_2}px`,
        width: '100%',
        maxWidth: '30rem'
      }}
    >
      <ExpandCollapse
        variant="iconWithText"
        isExpanded={isExpanded}
        expandText="Show error details"
        collapseText="Hide error details"
        onClick={() => setIsExpanded((prev) => !prev)}
      />
      {isExpanded && (
        <Stack
          role="alert"
          sx={{
            ...ERROR_SURFACE_SX,
            gap: 1,
            p: `${spacing.spacing_2}px`,
            width: '100%',
            textAlign: 'left'
          }}
        >
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            <SvgIcon
              inheritViewBox
              component={ERROR_ICON}
              sx={{ color: ERROR_ICON_COLOR }}
            />
            <Typography
              sx={{
                fontWeight: typography.fontWeight.semiBold,
                color: semanticColors.color.status.error.fg
              }}
            >
              Error details
            </Typography>
            <Box sx={{ flex: 1 }} />
            <CopyPasta text={errorDetails} tooltip="Copy error details" />
          </Stack>
          <Box
            component="pre"
            sx={{
              m: 0,
              px: 0.5,
              maxHeight: PRE_MAX_HEIGHT,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: (theme) => theme.typography.fontFamilyMonospace,
              fontSize: typography.fontSize.xs,
              color: semanticColors.color.text.primary
            }}
          >
            {errorDetails}
          </Box>
        </Stack>
      )}
    </Stack>
  );
};
